import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import './fiche-technique.css';

// Petits utilitaires partagés par la fiche technique (formats + pop-up courte).
export const dec2 = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const dec = (n, max = 2) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: max });
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
export const plural = (n, s = 's') => (Number(n) > 1 ? s : '');
export const errText = (e, fallback) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback);

// « Enregistrer les modifications ? » (01.1) : garde posée sur toute fermeture d'une saisie non enregistrée.
const Garde = ({ onSave, onDiscard, onCancel, busy }) => (
  <div className="ft-garde" role="alertdialog" aria-label="Enregistrer les modifications ?" data-testid="ft-garde">
    <p>Enregistrer les modifications ?</p>
    <div className="ft-modal-actions">
      <button type="button" className="ft-btn" onClick={onCancel} data-testid="ft-garde-annuler">Annuler</button>
      <button type="button" className="ft-btn" onClick={onDiscard} data-testid="ft-garde-ignorer">Ne pas enregistrer</button>
      <button type="button" className="ft-btn is-solid" disabled={busy} onClick={onSave} data-testid="ft-garde-enregistrer">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Enregistrer</button>
    </div>
  </div>
);

// Pop-up courte (saisie brève / confirmation) : fond crème, Escape et clic dehors ferment ; `dirty` + `onSave` activent la garde.
export const FicheModal = ({ open = true, onClose, kicker, title, children, wide = false, testId = 'ft-modal', closeOnOutside = true, dirty = false, onSave }) => {
  const [garde, setGarde] = useState(false);
  const [busy, setBusy] = useState(false);
  const tryClose = () => { if (dirty && onSave) setGarde(true); else onClose?.(); };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); tryClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose, dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const save = async () => { setBusy(true); try { const ok = await onSave(); if (ok !== false) onClose?.(); } finally { setBusy(false); setGarde(false); } };
  return createPortal(
    <div className="ft-modal-overlay" onClick={() => closeOnOutside && tryClose()} data-testid={`${testId}-overlay`}>
      <div className={`ft-modal ${wide ? 'is-wide' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <button type="button" className="ft-modal-x" onClick={tryClose} aria-label="Fermer" data-testid={`${testId}-close`}><X size={16} /></button>
        {kicker && <div className="ft-modal-kicker">{kicker}</div>}
        {title && <h3 className="ft-modal-title">{title}<span className="ft-dot">.</span></h3>}
        {garde ? <Garde busy={busy} onSave={save} onDiscard={() => { setGarde(false); onClose?.(); }} onCancel={() => setGarde(false)} /> : children}
      </div>
    </div>, document.body);
};

// Confirmation courte (suppression, retrait…) : un texte, Annuler + bouton sombre.
export const FicheConfirm = ({ open, onClose, kicker = 'Confirmation', title, text, action, busy, onConfirm, testId = 'ft-confirm' }) => (
  <FicheModal open={open} onClose={onClose} kicker={kicker} title={title} testId={testId}>
    <p className="text-sm text-[var(--p-4a443e)]">{text}</p>
    <div className="ft-modal-actions">
      <button type="button" className="ft-btn" onClick={onClose} data-testid={`${testId}-annuler`}>Annuler</button>
      <button type="button" className="ft-btn is-solid" disabled={busy} onClick={onConfirm} data-testid={`${testId}-ok`}>{busy ? <Loader2 size={14} className="animate-spin" /> : null} {action}</button>
    </div>
  </FicheModal>
);

// La direction voit toujours les coûts : si une vue arrive sans `droits` (vue partielle, aperçu), on lit le rôle de la session.
const DIRECTION = ['owner', 'manager', 'admin', 'super_admin', 'direction', 'directeur', 'gerant', 'gérant', 'consultant'];
export const voitCouts = (vue, user) => {
  if (vue?.droits && typeof vue.droits.couts === 'boolean') return vue.droits.couts || DIRECTION.includes(String(user?.role || '').toLowerCase());
  return DIRECTION.includes(String(user?.role || '').toLowerCase()) || /direct|gérant|gerant|patron/i.test(String(user?.poste || ''));
};
